import { create } from 'zustand';
import { QUEUE_VIEW_RECEIPT_HANDLES_MAX, queueReceiptState, type StorageQueueMessage, type StorageQueueReceivedMessage } from '../../../../types/azureStorage';
import { closeQueueDialogsForTab } from './queueDialogStore';

export type QueueMessageRow = StorageQueueMessage & Partial<Pick<StorageQueueReceivedMessage, 'receiptHandle' | 'visibleUntil' | 'nextVisibleOn' | 'expiryDeadline'>> & {
  lost?: boolean;
};
export type QueueMessageSet = 'peeked' | 'received';
export type QueuePendingAction = 'peek' | 'add' | 'receive' | 'update' | 'delete' | 'clear';

export type QueueMessagesRuntime = {
  connectionId: string;
  connectionGeneration?: number;
  approximateMessageCount?: number;
  latestPeekRequestId?: string;
  messageSet: QueueMessageSet;
  peekedById: Record<string, QueueMessageRow>;
  peekedOrder: string[];
  receivedById: Record<string, QueueMessageRow>;
  receivedOrder: string[];
  peekEpoch: number;
  /** Bumped when the received set empties locally; the host answers with a fresh peek (never an empty received view). */
  peekRequest: number;
  /** True from an emptied received set until the next peek answer lands (the host shows its spinner meanwhile). */
  awaitingPeek: boolean;
  pending: Partial<Record<QueuePendingAction, boolean>>;
  now: number;
};

/** `staleReceipts`: main's answer for the received handles the peek carried (review C M2). */
type PeekValue = { messages: StorageQueueMessage[]; approximateMessageCount?: number; connectionGeneration: number; staleReceipts?: { removed: string[]; lost: string[] } };
type PeekApplyResult = 'applied' | 'stale' | 'generation-changed';

type QueueMessagesState = {
  byTab: Record<string, QueueMessagesRuntime>;
  ensure(tabId: string, connectionId: string): void;
  beginPeek(tabId: string, requestId: string): void;
  applyPeek(tabId: string, requestId: string, value: PeekValue): PeekApplyResult;
  /** False when the tab's runtime is gone (the caller reports it; the rows are not shown anywhere). */
  applyReceive(tabId: string, rows: StorageQueueReceivedMessage[], generation: number): boolean;
  applyUpdate(tabId: string, messageId: string, text: string | undefined, result: { receiptHandle: string; visibleUntil: string; nextVisibleOn?: string; expiresOn?: string; expiryDeadline?: string }): void;
  applyDelete(tabId: string, messageId: string): void;
  markLost(tabId: string, messageId: string): void;
  showPeeked(tabId: string): void;
  clear(tabId: string): void;
  setPending(tabId: string, action: QueuePendingAction, pending: boolean): void;
  tick(tabId: string, now?: number): void;
  release(tabId: string): void;
  resetForConnection(connectionId: string): void;
  /** The connection generation was replaced (reconnect, in-place Reconnect — review F G1); the caller re-peeks. */
  connectionReplaced(tabId: string): void;
};

const rowsToMap = (rows: QueueMessageRow[]) => Object.fromEntries(rows.map((row) => [row.messageId, row]));

/**
 * Applies main's receipt answer to the received set: rows whose receipt main retired
 * (Clear, queue delete, another surface's delete) leave the view; service-refused ones
 * turn `lost` (no receipt actions). An emptied received view falls back to the peek.
 */
function reconcileReceived(runtime: QueueMessagesRuntime, stale: PeekValue['staleReceipts']): Pick<QueueMessagesRuntime, 'messageSet' | 'receivedById' | 'receivedOrder'> {
  const unchanged = { messageSet: runtime.messageSet, receivedById: runtime.receivedById, receivedOrder: runtime.receivedOrder };
  if (!stale || (stale.removed.length === 0 && stale.lost.length === 0)) return unchanged;
  const removed = new Set(stale.removed);
  const lost = new Set(stale.lost);
  const receivedOrder = runtime.receivedOrder.filter((id) => !removed.has(runtime.receivedById[id]?.receiptHandle ?? ''));
  const receivedById = Object.fromEntries(receivedOrder.map((id) => {
    const row = runtime.receivedById[id];
    return [id, row.receiptHandle && lost.has(row.receiptHandle) ? { ...row, lost: true } : row];
  }));
  const emptied = runtime.receivedOrder.length > 0 && receivedOrder.length === 0;
  return { messageSet: emptied ? 'peeked' : runtime.messageSet, receivedById, receivedOrder };
}

/** Opaque handles of the received rows a view still shows (sent with its peek so main can revalidate them). */
export const receivedReceiptHandles = (runtime: QueueMessagesRuntime | undefined): string[] =>
  runtime ? runtime.receivedOrder.map((id) => runtime.receivedById[id]?.receiptHandle).filter((handle): handle is string => typeof handle === 'string').slice(-QUEUE_VIEW_RECEIPT_HANDLES_MAX) : [];
const initialRuntime = (connectionId: string): QueueMessagesRuntime => ({
  connectionId, messageSet: 'peeked', peekedById: {}, peekedOrder: [], receivedById: {}, receivedOrder: [],
  peekEpoch: 0, peekRequest: 0, awaitingPeek: false, pending: {}, now: Date.now(),
});

/**
 * Coordinator ruling O1 / review Fable F2: an empty received set is never displayed. The view
 * returns to the peek, the pre-receive peek cache is dropped (it may still list the message just
 * deleted or taken), any in-flight peek is fenced, and the host starts a fresh peek.
 */
const emptiedToPeek = (runtime: QueueMessagesRuntime): Partial<QueueMessagesRuntime> => ({
  messageSet: 'peeked', peekedById: {}, peekedOrder: [], receivedById: {}, receivedOrder: [], latestPeekRequestId: undefined,
  peekEpoch: runtime.peekEpoch + 1, peekRequest: runtime.peekRequest + 1, awaitingPeek: true,
});

/** A received row the user can still act on (its receipt is neither elapsed, expired nor lost). */
const actionable = (row: QueueMessageRow | undefined, now: number): row is QueueMessageRow => {
  if (!row?.receiptHandle || !row.visibleUntil) return false;
  const state = queueReceiptState(row as { visibleUntil: string; expiresOn?: string; lost?: boolean }, now);
  return state === 'valid' || state === 'expiring';
};

/**
 * Review Fable F1: a later Receive accumulates. Rows whose receipts are still live stay actionable
 * (same generation only); a message received again keeps its position with the newest receipt.
 */
function mergeReceived(runtime: QueueMessagesRuntime, incoming: QueueMessageRow[], generation: number, now: number): QueueMessageRow[] {
  const sameGeneration = runtime.connectionGeneration === undefined || runtime.connectionGeneration === generation;
  const kept = sameGeneration ? runtime.receivedOrder.map((id) => runtime.receivedById[id]).filter((row) => actionable(row, now)) : [];
  const newest = new Map(incoming.map((row) => [row.messageId, row]));
  const merged = kept.map((row) => newest.get(row.messageId) ?? row);
  const keptIds = new Set(kept.map((row) => row.messageId));
  return [...merged, ...incoming.filter((row) => !keptIds.has(row.messageId))];
}

export const useQueueMessagesStore = create<QueueMessagesState>((set, get) => ({
  byTab: {},
  ensure: (tabId, connectionId) => set((state) => state.byTab[tabId] ? state : ({ byTab: { ...state.byTab, [tabId]: initialRuntime(connectionId) } })),
  beginPeek: (tabId, requestId) => set((state) => {
    const runtime = state.byTab[tabId];
    return runtime ? { byTab: { ...state.byTab, [tabId]: { ...runtime, latestPeekRequestId: requestId, pending: { ...runtime.pending, peek: true } } } } : state;
  }),
  applyPeek: (tabId, requestId, value) => {
    const runtime = get().byTab[tabId];
    if (!runtime || runtime.latestPeekRequestId !== requestId) return 'stale';
    if (runtime.connectionGeneration !== undefined && runtime.connectionGeneration !== value.connectionGeneration) {
      set((state) => ({ byTab: { ...state.byTab, [tabId]: { ...runtime, connectionGeneration: value.connectionGeneration, messageSet: 'peeked', peekedById: {}, peekedOrder: [], receivedById: {}, receivedOrder: [], peekEpoch: runtime.peekEpoch + 1, awaitingPeek: false, pending: { ...runtime.pending, peek: false } } } }));
      return 'generation-changed';
    }
    const rows = value.messages as QueueMessageRow[];
    const received = reconcileReceived(runtime, value.staleReceipts);
    const visibleSetChanged = received.messageSet === 'peeked' || received.receivedOrder.length !== runtime.receivedOrder.length;
    set((state) => ({ byTab: { ...state.byTab, [tabId]: { ...runtime, ...received, connectionGeneration: value.connectionGeneration, approximateMessageCount: value.approximateMessageCount, peekedById: rowsToMap(rows), peekedOrder: rows.map((row) => row.messageId), peekEpoch: visibleSetChanged ? runtime.peekEpoch + 1 : runtime.peekEpoch, awaitingPeek: false, pending: { ...runtime.pending, peek: false } } } }));
    return 'applied';
  },
  applyReceive: (tabId, rows, generation) => {
    const runtime = get().byTab[tabId];
    if (!runtime) return false;
    const incoming = rows as QueueMessageRow[];
    const next = mergeReceived(runtime, incoming, generation, Date.now());
    const base = { ...runtime, connectionGeneration: generation, latestPeekRequestId: undefined, pending: { ...runtime.pending, receive: false } };
    const changed = incoming.length > 0 || runtime.messageSet !== 'received' || next.length !== runtime.receivedOrder.length;
    set((state) => ({ byTab: { ...state.byTab, [tabId]: next.length === 0 ? { ...base, ...emptiedToPeek(runtime) } : {
      ...base, messageSet: 'received', receivedById: rowsToMap(next), receivedOrder: next.map((row) => row.messageId), peekEpoch: changed ? runtime.peekEpoch + 1 : runtime.peekEpoch,
    } } }));
    return true;
  },
  applyUpdate: (tabId, messageId, text, result) => set((state) => {
    const runtime = state.byTab[tabId]; const row = runtime?.receivedById[messageId];
    if (!runtime || !row) return state;
    return { byTab: { ...state.byTab, [tabId]: { ...runtime, receivedById: { ...runtime.receivedById, [messageId]: { ...row, ...(text !== undefined ? { text } : {}), ...result, lost: false } }, pending: { ...runtime.pending, update: false } } } };
  }),
  applyDelete: (tabId, messageId) => set((state) => {
    const runtime = state.byTab[tabId]; if (!runtime) return state;
    const receivedById = { ...runtime.receivedById }; delete receivedById[messageId];
    const receivedOrder = runtime.receivedOrder.filter((id) => id !== messageId);
    const pending = { ...runtime.pending, delete: false };
    if (receivedOrder.length === 0) return { byTab: { ...state.byTab, [tabId]: { ...runtime, ...emptiedToPeek(runtime), pending } } };
    return { byTab: { ...state.byTab, [tabId]: { ...runtime, receivedById, receivedOrder, peekEpoch: runtime.peekEpoch + 1, pending } } };
  }),
  markLost: (tabId, messageId) => set((state) => {
    const runtime = state.byTab[tabId]; const row = runtime?.receivedById[messageId];
    return runtime && row ? { byTab: { ...state.byTab, [tabId]: { ...runtime, receivedById: { ...runtime.receivedById, [messageId]: { ...row, lost: true } } } } } : state;
  }),
  showPeeked: (tabId) => set((state) => state.byTab[tabId] ? ({ byTab: { ...state.byTab, [tabId]: { ...state.byTab[tabId], messageSet: 'peeked' } } }) : state),
  clear: (tabId) => set((state) => {
    const runtime = state.byTab[tabId];
    // A cleared queue has no received set left: back to a freshly peeked view (review D D3 + ruling O1).
    return runtime ? { byTab: { ...state.byTab, [tabId]: { ...runtime, ...emptiedToPeek(runtime), pending: { ...runtime.pending, clear: false } } } } : state;
  }),
  setPending: (tabId, action, pending) => set((state) => state.byTab[tabId] ? ({ byTab: { ...state.byTab, [tabId]: { ...state.byTab[tabId], pending: { ...state.byTab[tabId].pending, [action]: pending } } } }) : state),
  tick: (tabId, now = Date.now()) => set((state) => state.byTab[tabId] ? ({ byTab: { ...state.byTab, [tabId]: { ...state.byTab[tabId], now } } }) : state),
  release: (tabId) => set((state) => { const byTab = { ...state.byTab }; delete byTab[tabId]; return { byTab }; }),
  // Main released every receipt of the previous generation: the received set is dead. A view that showed it returns to the
  // peek WITHOUT the pre-receive peek cache (ruling O1: spinner until the re-peek lands); in-flight peeks are fenced and the
  // old service generation is forgotten, so the re-peek's answer is applied rather than read as another generation change.
  connectionReplaced: (tabId) => set((state) => {
    const runtime = state.byTab[tabId];
    if (!runtime) return state;
    const hadReceived = runtime.receivedOrder.length > 0;
    return { byTab: { ...state.byTab, [tabId]: {
      ...runtime, connectionGeneration: undefined, latestPeekRequestId: undefined, messageSet: 'peeked', receivedById: {}, receivedOrder: [],
      ...(hadReceived ? { peekedById: {}, peekedOrder: [], awaitingPeek: true } : {}), peekEpoch: runtime.peekEpoch + 1,
    } } };
  }),
  resetForConnection: (connectionId) => set((state) => ({ byTab: Object.fromEntries(Object.entries(state.byTab).filter(([, runtime]) => runtime.connectionId !== connectionId)) })),
}));

export const queueRowsFor = (runtime: QueueMessagesRuntime | undefined): QueueMessageRow[] => {
  if (!runtime) return [];
  return runtime.messageSet === 'peeked' ? runtime.peekedOrder.map((id) => runtime.peekedById[id]).filter(Boolean) : runtime.receivedOrder.map((id) => runtime.receivedById[id]).filter(Boolean);
};
/** Tab release: drops the renderer runtime and closes the tab's open queue dialog (its cleanup cancels the request — review Fable F6). */
export const releaseQueueMessages = (tabId: string): void => {
  closeQueueDialogsForTab(tabId);
  useQueueMessagesStore.getState().release(tabId);
};
